import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clientVisitService } from "../../api/clientVisitService";
import Modal from "../../components/ui/Modal";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { todayManilaKey } from "../../utils/manilaTime";
import { cx } from "../../utils/classNames";
import {
  CLIENT_VISIT_KEY,
  clientsWithoutLocation,
  firstFileableDate,
  MAX_PURPOSE,
  NO_LOCATION_TEXT,
  validateVisit,
} from "./clientVisitUtils";
import ui from "../../components/ui/ui.module.css";
import styles from "./ClientVisits.module.css";

const FileClientVisitModal = ({ clients, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const today = todayManilaKey();

  const [date, setDate] = useState("");
  const [clientIds, setClientIds] = useState([]);
  const [purpose, setPurpose] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const text = search.trim().toLowerCase();
  const shown = text
    ? clients.filter((c) =>
        `${c.name} ${c.address ?? ""}`.toLowerCase().includes(text),
      )
    : clients;
  const noLocation = clientsWithoutLocation(
    clients.filter((c) => clientIds.includes(c.id)),
  );

  const toggle = (id) =>
    setClientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const mutation = useMutation({
    mutationFn: (payload) => clientVisitService.file(payload),
    onSuccess: () => {
      toast.success("Client visit submitted. Waiting for approval.");
      queryClient.invalidateQueries({ queryKey: [CLIENT_VISIT_KEY] });
      onClose();
    },
    onError: (err) =>
      setError(getErrorMessage(err, "Unable to submit the client visit.")),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = validateVisit({ date, clientIds, purpose }, today);
    setError(message);
    if (message) return;
    mutation.mutate({ date, clientIds, purpose: purpose.trim() });
  };

  return (
    <Modal
      as="form"
      onSubmit={handleSubmit}
      title="File a client visit"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className={cx(ui.button, ui.secondary)}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={cx(ui.button, ui.primary)}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Submitting…" : "Submit Request"}
          </button>
        </>
      }
    >
      <div className={ui.formStack}>
        <div>
          <label htmlFor="visit-date" className={ui.label}>
            Date *
          </label>
          <input
            id="visit-date"
            type="date"
            className={ui.input}
            min={firstFileableDate(today)}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <small className={ui.hint}>
            File before the day of the visit. One request per day; it can list
            several clients.
          </small>
        </div>

        <div>
          <label htmlFor="visit-client-search" className={ui.label}>
            Clients *
          </label>
          {clients.length > 6 && (
            <input
              id="visit-client-search"
              type="search"
              className={cx(ui.input, "mb-2")}
              placeholder="Search clients"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <div className={styles.clientList} role="group" aria-label="Clients">
            {shown.length === 0 && (
              <div className={ui.muted}>No clients found.</div>
            )}
            {shown.map((c) => (
              <label key={c.id} className={ui.checkbox}>
                <input
                  type="checkbox"
                  checked={clientIds.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                <span>
                  {c.name}
                  {c.address && (
                    <span className={cx(ui.muted, "d-block")}>{c.address}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          {clientIds.length > 0 && (
            <small className={ui.hint}>{clientIds.length} selected</small>
          )}
        </div>

        {noLocation.length > 0 && (
          <div className={cx(ui.alert, ui.alertWarning)}>
            {noLocation.map((c) => c.name).join(", ")} {NO_LOCATION_TEXT}
          </div>
        )}

        <div>
          <label htmlFor="visit-purpose" className={ui.label}>
            Purpose *
          </label>
          <textarea
            id="visit-purpose"
            rows={3}
            maxLength={MAX_PURPOSE}
            className={cx(ui.input, ui.textarea)}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Quarterly payroll review"
          />
          <small className={ui.hint}>
            {purpose.length}/{MAX_PURPOSE}
          </small>
        </div>

        {error && <div className={cx(ui.alert, ui.alertDanger)}>{error}</div>}
      </div>
    </Modal>
  );
};

export default FileClientVisitModal;
